// 戴冠獣のしかかり「上を向いて溜める吠え」FX (2026-10-06・のしかかり (戴冠獣 v2＝既定・旧は ?houndKit=v1) の中だけで出る)。
//   ユーザー逐語 (2026-10-06):「上を向いて溜めている時、吠えているようなエフェクトが欲しい」
//     「口から煙を吐いているようにしか見えないね」(A2/A3) → 「D2だけで良い。D3の表現は記号的すぎる。」
//     「獣の姿勢は崩さずに岩も出さずに」。見本 = そざい/fx/hound-pounce-howl-1006/howl_D2.png
//
// 何を出すか (描くだけ・判定と動きは1pxも変えない)。口から何も出さない。声を「空気の圧」で見せる:
//   ① 背景の波打ち: 柱・旗・窓を横の細い帯に切り、口の高さを中心に上下へ波打たせる (獣・主人公・HUD は動かさない)。
//   ② 紫の圧の帯: 口から広がる、太くてやわらかい途切れた弧が数枚、だんだん広く薄くなって消える (獣の背面)。
//   ③ 足元の砂ぼこり: 左右の床の低い所で、細かい淡い粒がふわっと外へ流れる。
//   岩・石・破片・煙・輪・細い線は出さない。強さは立ち上がり→維持→のしかかりの跳びが始まる頃にはゼロ。
import type { Boss } from './types'
import { getHoundArtOld, phaseProgressOf } from './boss_pose_timeline'
import { hash01 } from './fx_kit'
import { houndRoarMouth } from './hound_road1_1031'
import { ARENA } from './balance'
import { sw } from './purple_swap'

/** のしかかりの溜め 1420ms のうち、立ち上がって構える部分 (boss_pose_timeline の houndPounce と同じ値)。 */
const HOLD_MS = 1100
const TELEGRAPH_MS = 1420
const RISE_MS = 240
const FADE_START_MS = 1000
const FADE_END_MS = 1180

let testOverride: boolean | undefined
export function setHoundHowlForTest(enabled: boolean | undefined): void { testOverride = enabled }
/** 既定 = 出す。比べたい時だけ DEV で `?houndHowl=off`。 */
export function houndHowlEnabled(): boolean {
  if (testOverride !== undefined) return testOverride
  // 配布ビルドには URL の読み取りを入れない (scripts/check_dev_leak.mjs)。
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get('houndHowl') !== 'off'
  }
  return true
}

/** のしかかりの溜め中なら、溜めの頭からの経過 ms (拍で溜めを縮めた時も 1420ms 基準に直す)。それ以外は null。 */
export function houndHowlMs(b: Boss): number | null {
  if (b.id !== 'dream-hound' || b.state !== 'telegraph' || b.currentAttack?.id !== 'houndPounce') return null
  const p = phaseProgressOf(b)
  return p.phase === 'telegraph' ? p.progress * TELEGRAPH_MS : null
}

const easeOut = (u: number): number => 1 - (1 - u) * (1 - u)
const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))

/** 吠えの強さ 0..1。立ち上がりで素早く上がり、跳びの始まる頃に消える。 */
export function houndHowlIntensity(ms: number): number {
  if (ms <= 0 || ms >= FADE_END_MS) return 0
  const rise = easeOut(clamp01(ms / RISE_MS))
  const fade = ms <= FADE_START_MS ? 1 : 1 - clamp01((ms - FADE_START_MS) / (FADE_END_MS - FADE_START_MS))
  return rise * fade
}

export type HowlAnchor = { x: number; y: number; dir: 1 | -1; footY: number; height: number }

/**
 * 口の位置 (世界座標)。立ち上がって頭を反らした絵の紫の口の中心＝体の中心から顔の向きへ高さの0.22・足から高さの1.45。
 * 10-07: 待機を土台に描き直した houndPounce_rearup で測り直した (描画と同じ置き方＝不透明の下端が床・中心が boss.x・面積正規化。
 *   原画で口は中心から前へ 176px・足から 1142px、面積 486,973)。旧の絵 houndExecution_windup (?houndArt=old) は 1.3。
 */
export function houndMouthAnchor(b: Boss, footY: number, height: number): HowlAnchor {
  const dir: 1 | -1 = b.facing === 'right' ? 1 : -1
  // 10-31 道1の吠えの描き直し (DEV ?houndRoar=a|b|c) は案ごとに測った口の位置。
  const roar = houndRoarMouth()
  const up = roar?.up ?? (getHoundArtOld() ? 1.3 : 1.45)
  const fwd = roar?.fwd ?? 0.22
  return { x: b.x + dir * height * fwd, y: footY - height * up, dir, footY, height }
}

/**
 * 背景を横へずらす量 (世界 px)。口の高さを中心に、上は広く・下 (床) は早く静まる。
 * 帯が口から外へ流れて見えるよう、位相は口からの距離で進める。
 */
export function houndHowlWarpDx(yWorld: number, mouthY: number, ms: number, k: number): number {
  const dy = yWorld - mouthY
  const reach = dy < 0 ? 250 : 150
  const env = Math.exp(-(dy / reach) * (dy / reach))
  const pulse = 0.78 + 0.22 * Math.sin(ms * 0.012)
  const wave = Math.sin(Math.abs(dy) / 42 - ms * 0.012) * 4.6 + Math.sin(dy / 21 + ms * 0.017) * 1.1
  return wave * env * pulse * k
}

let scratch: HTMLCanvasElement | null = null

/** ① 背景の波打ち。drawBackground の直後 (世界・獣・主人公を描く前) に呼ぶ。 */
export function applyHoundHowlWarp(ctx: CanvasRenderingContext2D, b: Boss, footY: number, height: number): void {
  if (!houndHowlEnabled()) return
  const ms = houndHowlMs(b)
  if (ms === null) return
  const k = houndHowlIntensity(ms)
  if (k < 0.02) return
  const mouth = houndMouthAnchor(b, footY, height)
  const source = ctx.canvas
  if (!scratch) scratch = document.createElement('canvas')
  if (scratch.width !== source.width || scratch.height !== source.height) {
    scratch.width = source.width
    scratch.height = source.height
  }
  const sctx = scratch.getContext('2d')
  if (!sctx) return
  const m = ctx.getTransform()
  const sy0 = Math.max(0, Math.floor(m.f + (mouth.y - 300) * m.d))
  const sy1 = Math.min(source.height, Math.ceil(m.f + (ARENA.floorY - 6) * m.d))
  if (sy1 <= sy0) return
  sctx.setTransform(1, 0, 0, 1, 0, 0)
  sctx.globalCompositeOperation = 'copy'
  sctx.drawImage(source, 0, sy0, source.width, sy1 - sy0, 0, sy0, source.width, sy1 - sy0)
  sctx.globalCompositeOperation = 'source-over'
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  const step = Math.max(2, Math.round(2 * m.d))
  for (let sy = sy0; sy < sy1; sy += step) {
    const sh = Math.min(step, sy1 - sy)
    const yWorld = (sy + sh / 2 - m.f) / m.d
    const dx = houndHowlWarpDx(yWorld, mouth.y, ms, k) * m.a
    if (Math.abs(dx) < 0.05) continue
    ctx.drawImage(scratch, 0, sy, source.width, sh, dx, sy, source.width, sh)
  }
  ctx.restore()
}

const PULSE_GAP_MS = 250
const PULSE_LIFE_MS = 880
const PULSE_COUNT = 5

// 弧の向き: 真上から顔の向きへ少し倒した方を中心に、大きく開く。途切れは毎パルスの乱数で決まる。
function drawPressureBand(ctx: CanvasRenderingContext2D, a: HowlAnchor, pulse: number, u: number, k: number): void {
  const reach = 46 + 330 * easeOut(u)
  const base = -Math.PI / 2 + a.dir * 0.42
  const half = 1.25 + 0.2 * u
  const fadeIn = clamp01(u / 0.12)
  const alpha = k * fadeIn * Math.pow(1 - u, 1.4) * 0.36
  if (alpha < 0.004) return
  const width = 24 + 34 * u
  const parts = 3 + Math.floor(hash01(pulse * 3.7) * 2)
  ctx.lineCap = 'round'
  for (let s = 0; s < parts; s++) {
    const a0 = base - half + (s / parts) * half * 2 + hash01(pulse * 11.3 + s) * 0.18
    const a1 = base - half + ((s + 0.62 + hash01(pulse * 5.1 + s * 2.9) * 0.2) / parts) * half * 2
    for (const [wm, am] of [[1.9, 0.16], [1.45, 0.22], [1.05, 0.3], [0.65, 0.4], [0.32, 0.5]] as const) {
      ctx.lineWidth = width * wm
      ctx.strokeStyle = `rgba(${sw('unparry', '156,112,236')},${(alpha * am).toFixed(4)})`
      ctx.beginPath()
      ctx.arc(a.x, a.y, reach * (0.94 + 0.12 * hash01(pulse + s * 1.7)), a0, a1)
      ctx.stroke()
    }
  }
}

// 左右の床に低く流れる細かい淡い粒。状態を持たず、時刻と粒番号だけで位置が決まる。
function drawFloorDust(ctx: CanvasRenderingContext2D, a: HowlAnchor, ms: number, k: number): void {
  const COUNT = 14
  for (const side of [-1, 1] as const) {
    for (let j = 0; j < COUNT; j++) {
      const seed = j * 4.13 + (side > 0 ? 50 : 0)
      const period = 900 + hash01(seed) * 500
      const u = ((ms + hash01(seed + 1) * period) % period) / period
      const travel = 70 + 190 * hash01(seed + 2)
      const x = a.x - a.dir * a.height * 0.22 + side * (26 + travel * easeOut(u))
      const lift = (8 + 26 * hash01(seed + 3)) * Math.sin(Math.PI * Math.min(1, u * 1.15))
      const y = a.footY - 3 - lift
      const r = 10 + 16 * u + 8 * hash01(seed + 4)
      const alpha = k * 0.34 * Math.sin(Math.PI * u) * (0.6 + 0.4 * hash01(seed + 5))
      if (alpha < 0.004) continue
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      g.addColorStop(0, `rgba(160,150,184,${alpha.toFixed(4)})`)
      g.addColorStop(0.55, `rgba(150,140,176,${(alpha * 0.45).toFixed(4)})`)
      g.addColorStop(1, 'rgba(130,120,160,0)')
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.ellipse(x, y, r * 2.1, r * 0.6, 0, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

/** ②③ 圧の帯と足元の砂ぼこり。世界の層・獣を描く直前 (獣の背面) に呼ぶ。 */
export function drawHoundHowlBehind(ctx: CanvasRenderingContext2D, b: Boss, footY: number, height: number): void {
  if (!houndHowlEnabled()) return
  const ms = houndHowlMs(b)
  if (ms === null) return
  const k = houndHowlIntensity(ms)
  if (k < 0.02) return
  const a = houndMouthAnchor(b, footY, height)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 紫の技を替える比較版 (DEV ?purpleSwap・src/purple_swap.ts): 弾けない技なので圧の帯と霞を 'unparry' へ。砂ぼこりは今のまま。
  // 口のまわりの空気がうっすら紫に明るむ (見本 D2 の上の広間の霞)。
  const haze = ctx.createRadialGradient(a.x, a.y, 0, a.x, a.y, 300)
  haze.addColorStop(0, `rgba(${sw('unparry', '120,84,200')},${(0.16 * k).toFixed(4)})`)
  haze.addColorStop(1, `rgba(${sw('unparry', '120,84,200')},0)`)
  ctx.fillStyle = haze
  ctx.fillRect(a.x - 300, a.y - 300, 600, 600)
  for (let p = 0; p < PULSE_COUNT; p++) {
    const age = ms - p * PULSE_GAP_MS
    if (age < 0 || age > PULSE_LIFE_MS) continue
    drawPressureBand(ctx, a, p, age / PULSE_LIFE_MS, k)
  }
  ctx.restore()
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'   // 暗い床の上でも淡い粒が見えるよう足し合わせる
  drawFloorDust(ctx, a, ms, k)
  ctx.restore()
}

export const HOUND_HOWL_HOLD_MS = HOLD_MS
